import { useMemo } from "react";
import { gql, useQuery } from "@apollo/client";
import { SecurityGroup } from "api/types";
import { fallbackLanguage } from "i18n";
import { useKeycloak } from "providers/KeycloakProvider";
import { PORTFOLIO_BASIC_FIELDS } from "./portfolioFragments";

// PORTFOLIO_BASIC_FIELDS now lives in ./portfolioFragments (a dependency-free
// leaf) to break a circular import. Re-exported here so existing consumers that
// import it from this module keep working.
export { PORTFOLIO_BASIC_FIELDS };

export const CONTACT_INFO_QUERY = gql`
  ${PORTFOLIO_BASIC_FIELDS}
  query GetContactInfo($contactId: Long) {
    contact(id: $contactId) {
      id
      contactId
      name
      language {
        locale
      }
      representees(onlyDirectRepresentees: true) {
        name
        id
        contactId
        portfolios {
          ...PortfolioBasicFields
        }
      }
      assetManagerPortfolios {
        primaryContact {
          contactId
          name
        }
      }
      portfolios {
        ...PortfolioBasicFields
      }
    }
  }
`;

// New lighter queries
export const CONTACT_INFO_BASE_QUERY = gql`
  query GetContactInfoBase($contactId: Long) {
    contact(id: $contactId) {
      id
      contactId
      name
      language {
        locale
      }
      representees(onlyDirectRepresentees: true) {
        id
        contactId
        name
      }
      assetManagerPortfolios {
        primaryContact {
          contactId
          name
        }
      }
    }
  }
`;

export const CONTACT_PORTFOLIOS_QUERY = gql`
  query GetContactPortfolios($contactId: Long) {
    contact(id: $contactId) {
      id
      portfolios {
        id
        name
        status
        currency {
          securityCode
        }
        portfolios {
          id
          name
          status
          currency {
            securityCode
          }
        }
      }
    }
  }
`;

export enum PortfolioStatus {
  Active = "A",
  Passive = "P",
  Closed = "C",
}

export enum PortfolioGroups {
  CANCEL_ORDER = "CP_CANCEL",
  DEPOSIT = "CP_DEPOSIT",
  WITHDRAW = "CP_WITHDRAWAL",
  TRADE = "CP_TRADING",
  HIDE = "CP_HIDE_PF",
  MONTHLY_INVESTMENTS = "CP_MONTHLYINVESTMENTS",
  MONTHLY_SAVINGS = "CP_MONTHLYSAVINGS",
}

export interface PortfolioGroup {
  id: number;
  code: PortfolioGroups;
}

export interface Representee {
  id: number;
  name: string;
  contactId: string;
  portfolios?: Portfolio[];
  representees?: Representee[];
}

export interface AssetManagerPortfolios {
  primaryContact: {
    contactId: string;
    name: string;
  };
}

export interface Portfolio {
  id: number;
  name: string;
  portfolioName?: string;
  status: string;
  shortName: string;
  parentPortfolios?: {
    id: number;
  }[];
  portfolios: Portfolio[]; // sub-portfolios
  currency: {
    securityCode: string;
    amountDecimalCount: number;
  };
  portfolioGroups: PortfolioGroup[];
  securityGroups?: SecurityGroup[];
}

export interface ContactInfoQuery {
  contact?: {
    id: number;
    contactId: string;
    name: string;
    representees: Representee[];
    assetManagerPortfolios: AssetManagerPortfolios[];
    language: {
      locale: string;
    };
    portfolios?: Portfolio[];
  };
}

export const useGetContactInfo = (callAPI = false, id?: string | number) => {
  const { linkedContact } = useKeycloak();

  const contactId = id?.toString() || linkedContact?.toString();

  // 1. Fetch base contact info fast
  const {
    loading: baseLoading,
    error: baseError,
    data: baseData,
  } = useQuery<{ contact: ContactInfoQuery["contact"] }>(
    CONTACT_INFO_BASE_QUERY,
    {
      variables: { contactId },
      fetchPolicy: "cache-first",
    }
  );

  // 2. Fetch portfolios separately (skip until we know contactId)
  const {
    loading: pfLoading,
    error: pfError,
    data: pfData,
    refetch: refetchPortfolios,
  } = useQuery<{ contact: { id: number; portfolios: Portfolio[] } }>(
    CONTACT_PORTFOLIOS_QUERY,
    {
      variables: { contactId },
      fetchPolicy: callAPI ? "cache-and-network" : "cache-first",
      skip: !contactId,
    }
  );

  const activeAndPassivePortfolios = useMemo<Portfolio[]>(
    () =>
      !pfData?.contact?.portfolios?.length
        ? []
        : removeClosed(pfData.contact.portfolios),
    [pfData?.contact?.portfolios]
  );

  return {
    loading: baseLoading || pfLoading,
    error: baseError || pfError,
    data: baseData && {
      contactId: baseData.contact?.id,
      _contactId: baseData.contact?.contactId,
      portfolios: activeAndPassivePortfolios,
      locale: baseData.contact?.language?.locale || fallbackLanguage,
      portfoliosCurrency:
        activeAndPassivePortfolios?.[0]?.currency?.securityCode,
      representees: baseData.contact?.representees,
      assetManagerPortfolios: baseData.contact?.assetManagerPortfolios,
      name: baseData.contact?.name,
    },
    refetch: refetchPortfolios,
  };
};

/**
 * Removes closed portfolios (recursively on all sub portfolios as well).
 * @param portfolios Raw portfolio data from FA
 * @returns active/pending portfolios
 */
export const removeClosed = (portfolios: Portfolio[]): Portfolio[] => {
  const result: Portfolio[] = [];
  portfolios?.forEach((portfolio: Portfolio) => {
    if (portfolio.status !== PortfolioStatus.Closed) {
      const portfoliosCleaned = removeClosed(portfolio.portfolios);
      result.push({
        ...portfolio,
        portfolios: portfoliosCleaned,
      });
    }
  });
  return result;
};
